import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { Calendar, Download, RefreshCw, Loader2, BarChart3, ChevronDown, ChevronRight, Search, X, FileDown } from 'lucide-react';
import ConsistencyBadge from '../../components/ConsistencyBadge';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { plannerBucket, hasNonBucketMonth } from './plannerBucket';
import { isBrandingCampaign } from '../../utils/brandingClassifier';
// Branding raporundan düşen kampanyalar — faturanın hariç listesinden yalnızca
// `raporlardanCikar` işaretliler (Yataş Bedding: Yunanistan). Dormy / Urban Mood KALIR.
import { haricleriAyikla } from '../../utils/haricKampanyalar';
import { useMetaReach, useReachOf, MetaReachContext, mrNew, mrAddRow, sumMetrics, withDedupReach } from '../../utils/metaReachDedup';

/* ── Sabitler ────────────────────────────────────────────────────────── */

const API = 'https://api.glowytics.com';

const BRANDING_COLS = [
  { key: 'butce',    label: 'Bütçe',    format: 'currency' },
  { key: 'gosterim', label: 'Gösterim', format: 'number'   },
  { key: 'erisim',   label: 'Erişim',   format: 'number'   },
  { key: 'izlenme',  label: 'İzlenme',  format: 'number'   },
  { key: 'trafik',   label: 'Trafik',   format: 'number'   },
  { key: 'tiklama',  label: 'Tıklama',  format: 'number'   },
  { key: 'gelir',    label: 'Gelir',    format: 'currency' },
  { key: 'roas',     label: 'ROAS',     format: 'roas'     },
];

const YT_COLS = [
  { key: 'butce',       label: 'Bütçe',        format: 'currency' },
  { key: 'gosterim',    label: 'Gösterim',     format: 'number'   },
  { key: 'izlenme',     label: 'İzlenme',      format: 'number'   },
  { key: 'izlenmeOrani', label: 'İzlenme Oranı', format: 'percent'  },
];

const MONTH_LABELS_TR = [
  'Ocak', 'Subat', 'Mart', 'Nisan', 'Mayis', 'Haziran',
  'Temmuz', 'Agustos', 'Eylul', 'Ekim', 'Kasim', 'Aralik',
];
const TARGET_PLANNER_YEAR = 2026;

/* ── Yardımcı ────────────────────────────────────────────────────────── */

const fmtDate = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const getLastCompletedMonthRange = () => {
  const now = new Date();
  const startDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const endDate = new Date(now.getFullYear(), now.getMonth(), 0);
  return { startDate, endDate };
};
const getComparisonPeriods = (startDate, endDate, compareStartDate = null, compareEndDate = null) => {
  const curStart = new Date(startDate);
  const curEnd = new Date(endDate);
  curStart.setHours(0, 0, 0, 0);
  curEnd.setHours(23, 59, 59, 999);
  let prevStart;
  let prevEnd;
  if (compareStartDate && compareEndDate) {
    prevStart = new Date(compareStartDate);
    prevEnd = new Date(compareEndDate);
    prevStart.setHours(0, 0, 0, 0);
    prevEnd.setHours(23, 59, 59, 999);
  } else {
    const periodMs = curEnd.getTime() - curStart.getTime();
    prevEnd = new Date(curStart.getTime() - 1);
    prevStart = new Date(prevEnd.getTime() - periodMs);
  }
  return { curStart, curEnd, prevStart, prevEnd };
};

const fmt = (value, format) => {
  if (value == null || value === '') return '–';
  switch (format) {
    case 'currency':
      return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(value);
    case 'number':
      return new Intl.NumberFormat('tr-TR').format(value);
    case 'percent':
      return typeof value === 'number' ? `%${value.toFixed(2)}` : '–';
    case 'roas':
      return typeof value === 'number' && isFinite(value) ? `${value.toFixed(2)}x` : '–';
    default:
      return value;
  }
};

// Sınıflandırma tek yerden: ortak `brandingClassifier`. Marka hepsi-branding
// listesindeyse (Divanev — e-ticaret yok, GA4'te purchase hiç yok) mecra fark
// etmeksizin her kampanya branding sayılır; değilse ad sinyali → objective/kampanya tipi.
const currentBrandId = () => localStorage.getItem('currentBrandId');
const isBranding = (name, campaignType) =>
  isBrandingCampaign({ name, platform: 'google', campaignType, brandId: currentBrandId() });
const isMetaBranding = (name, objective) =>
  isBrandingCampaign({ name, platform: 'meta', objective, brandId: currentBrandId() });

const isYoutubeCampaign = (name, campaignType) => {
  const n = (name || '').toString().toLowerCase();
  const t = (campaignType || '').toString().toLowerCase();
  return n.includes('youtube') || t.includes('youtube') || t.includes('video');
};
const hdr = (brandId) => ({
  'X-Brand-ID': brandId,
  'Authorization': `Bearer ${localStorage.getItem('token')}`,
  'Content-Type': 'application/json',
});

const MANUAL_BRANDING_LABELS = ['medya satınalma', 'medya satinalma', 'pinterest', 'yandex'];

// Report-4'ten hariç tutulacak satırlar (Google/Meta API'den geliyor veya revenue)
const EXCLUDE_FROM_MANUAL_BRANDING = [
  'google_ads', 'meta_ads', 'google_ads_performance', 'meta_ads_performance',
  'google_ads_branding', 'meta_ads_branding',
  'analytics_revenue', 'analytics_revenue_performance', 'analytics_revenue_branding',
];

const sumCols = (cols, items, dataKey) => {
  const t = {};
  cols.forEach(c => {
    if (c.format === 'percent' || c.format === 'roas') return;
    let sum = 0, found = false;
    items.forEach(item => {
      const src = dataKey ? item[dataKey] : item;
      const v = src?.[c.key];
      if (v != null) { sum += v; found = true; }
    });
    if (found) t[c.key] = sum;
  });
  if (t.butce && t.gelir) t.roas = t.gelir / t.butce;
  return t;
};

const normalizeLabel = (v) => (v || '')
  .toString()
  .toLowerCase()
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .trim();

function daysOverlap(startA, endA, startB, endB) {
  const start = startA > startB ? startA : startB;
  const end = endA < endB ? endA : endB;
  if (end < start) return 0;
  return Math.floor((end - start) / (1000 * 60 * 60 * 24)) + 1;
}

function listYearMonthInRange(startDate, endDate) {
  const start = new Date(startDate.getFullYear(), startDate.getMonth(), 1);
  const end = new Date(endDate.getFullYear(), endDate.getMonth(), 1);
  const periods = [];
  const cursor = new Date(start);
  while (cursor <= end) {
    periods.push({ year: cursor.getFullYear(), month: cursor.getMonth() + 1 });
    cursor.setMonth(cursor.getMonth() + 1);
  }
  return periods;
}

function buildManualBrandingRowsFromPlans(plansByYear, rangeStart, rangeEnd) {
  const start = new Date(rangeStart);
  const end = new Date(rangeEnd);
  start.setHours(0, 0, 0, 0);
  end.setHours(23, 59, 59, 999);

  // key -> { key, label, butce, gosterim, erisim, izlenme, trafik }
  const byKey = new Map();

  const addToBucket = (r, year, bucketKey, bucketLabel) => {
    if (!byKey.has(bucketKey)) byKey.set(bucketKey, { key: bucketKey, label: bucketLabel, butce: 0, gosterim: 0, erisim: 0, izlenme: 0, trafik: 0, tiklama: 0 });
    const bucket = byKey.get(bucketKey);
    const mv = r.metricsValues || {};
    for (let month = 1; month <= 12; month += 1) {
      // Kova AY BAZLI: o ay performans işaretliyse branding raporuna girmez —
      // satırın anahtarı `_branding` olsa bile. (report-3 aynı kuralın tersini uygular,
      // böylece aynı para iki raporda birden sayılmaz.)
      if (plannerBucket(r, month) === 'performance') continue;
      const monthStart = new Date(year, month - 1, 1);
      const monthEnd = new Date(year, month, 0, 23, 59, 59, 999);
      const overlap = daysOverlap(start, end, monthStart, monthEnd);
      if (overlap <= 0) continue;
      const daysInMonth = new Date(year, month, 0).getDate();
      const ratio = overlap / daysInMonth;
      const raw = Number(r.values?.[month] ?? r.values?.[String(month)] ?? 0) || 0;
      bucket.butce += raw * ratio;
      const m = mv[month] ?? mv[String(month)] ?? {};
      bucket.gosterim += (Number(m.gosterim) || 0) * ratio;
      bucket.erisim += (Number(m.erisim) || 0) * ratio;
      bucket.izlenme += (Number(m.izlenme) || 0) * ratio;
      bucket.trafik += (Number(m.trafik) || 0) * ratio;
      bucket.tiklama += (Number(m.tiklama) || 0) * ratio;
    }
  };

  plansByYear.forEach(({ year, rows }) => {
    (rows || []).forEach((r) => {
      if ((r.metricType || '').toLowerCase() !== 'spend') return;
      const rowKey = (r.key || '').toString();
      if (EXCLUDE_FROM_MANUAL_BRANDING.includes(rowKey)) return;
      // Performans kovasındaki kalemler branding raporuna ASLA girmez
      // (ayrım net olmalı — performans report-3'e aittir). Ay bazlı kova geldiğinden
      // beri eleme satır seviyesinde değil AY seviyesinde yapılıyor: satırın en az bir
      // ayı branding ise satır geçer, performans ayları addToBucket içinde atlanır.
      if (!hasNonBucketMonth(r, 'performance')) return;

      const labelNorm = normalizeLabel(r.label);
      const isKnown = MANUAL_BRANDING_LABELS.includes(labelNorm);
      // 🔴 Planlayıcıda satır BRANDING mi PERFORMANS mı, anahtarın önekinde duruyor
      // (`custom_branding_*` / `custom_performance_*`). Eskiden yalnız "elle
      // eklenmiş mi" diye bakılıyordu, o yüzden PERFORMANS satırları da branding
      // tablosuna giriyordu: Yataş Bedding'de Bing/Yandex/Cimri/Akakçe — Ağustos
      // 2026'da 168.022 ₺ performans harcaması branding sayılıyor, üstelik Yandex
      // ve Bing hem branding hem performans satırıyla İKİ KEZ listeleniyordu.
      // `bucket` sütunu bu satırlarda BOŞ, o yüzden ay bazlı eleme de yakalamıyor.
      if (rowKey.includes('_performance')) return;
      const isBrandingKey = rowKey.includes('_branding');
      const isCustom = r.isSystem === false;

      if (!isKnown && !isBrandingKey && !isCustom) return;

      if (isKnown) {
        let bucketLabel = 'Medya Satınalma';
        if (labelNorm.includes('pinterest')) bucketLabel = 'Pinterest';
        else if (labelNorm.includes('yandex')) bucketLabel = 'Yandex';
        addToBucket(r, year, rowKey, bucketLabel);
      } else {
        const bucketKey = rowKey || `custom_${labelNorm.replace(/\s+/g, '_')}`;
        const bucketLabel = (r.label || bucketKey).trim() || 'Manuel';
        addToBucket(r, year, bucketKey, bucketLabel);
      }
    });
  });

  return Array.from(byKey.values())
    .map((x) => ({
      key: x.key,
      label: x.label,
      isManual: true,
      data: {
        butce: Math.round(x.butce),
        gosterim: Math.round(x.gosterim),
        erisim: Math.round(x.erisim),
        izlenme: Math.round(x.izlenme),
        trafik: Math.round(x.trafik),
      },
      campaigns: [],
    }));
}

function buildMonthlyBrandingBudgetFromYearRows(rows, year = TARGET_PLANNER_YEAR) {
  const monthly = Object.fromEntries(Array.from({ length: 12 }, (_, i) => [i + 1, 0]));
  const isBrandingRow = (r) => {
    const key = (r.key || '').toLowerCase();
    const label = normalizeLabel(r.label);
    return (
      key.includes('_branding') ||
      key.startsWith('custom_branding_') ||
      label.includes('branding')
    );
  };

  (rows || []).forEach((r) => {
    if ((r.metricType || '').toLowerCase() !== 'spend') return;
    if (!isBrandingRow(r)) return;
    for (let m = 1; m <= 12; m += 1) {
      monthly[m] += Number(r.values?.[m] ?? r.values?.[String(m)] ?? 0) || 0;
    }
  });

  return MONTH_LABELS_TR.map((month, idx) => ({
    month,
    amount: Math.round(monthly[idx + 1] || 0),
    year,
  }));
}

/* ── Report-6 (medya satın alma) dip toplam → branding satırı ────────── */

function parseTurkishNumber(str) {
  if (!str || str === '-' || str === '–') return 0;
  const cleaned = str.replace(/[₺$€\s]/g, '').replace(/\./g, '').replace(/,/g, '.');
  return Number(cleaned) || 0;
}

/**
 * TikTok BRANDING toplamı — hem tablo satırı hem üstteki aylık "gerçekleşen" satırı
 * aynı fonksiyonu kullanır. (Yataş, 6 Eki 2026: üst satır TikTok'u hiç saymıyordu,
 * "toplam branding bütçe ile yukarıdaki total tutmuyor" — fark birebir TikTok'tu.)
 */
function tiktokBrandingTotals(rows, brandId) {
  const tkNameMap = buildLatestNameMap(rows);
  return rows.reduce((acc, r) => {
    // Sadece BRANDING kampanyaları — ortak sınıflandırıcı karar verir.
    // Performans kampanyaları branding raporuna girmez; onlar report-3'te.
    // İsim, campaign_id'nin en son günündeki ismine göre (sonradan rename'i yakalar).
    const cname = latestCampaignName(tkNameMap, r);
    if (!isBrandingCampaign({ name: cname, platform: 'tiktok', objective: r.campaign_objective, brandId })) return acc;
    acc.butce    += Number(r.spend)              || 0;
    acc.gosterim += Number(r.impressions)        || 0;
    acc.erisim   += Number(r.reach)              || 0;
    // İzlenme = video_play_actions (TikTok Ads Manager'daki "Video İzlenme").
    // video_views_p100 %100 tamamlamadır; diğer mecralar düz video_views ile
    // toplandığı için burada da panel metriği kullanılır.
    acc.izlenme  += Number(r.video_play_actions) || 0;
    acc.trafik   += Number(r.ga4_sessions)       || 0;
    acc.tiklama  += Number(r.clicks)             || 0;
    return acc;
  }, { butce: 0, gosterim: 0, erisim: 0, izlenme: 0, trafik: 0, tiklama: 0 });
}

function aggregateMediaBuyingFromSaved(savedFormats) {
  let butce = 0;
  let gosterim = 0;
  let erisim = 0;
  let tiklama = 0;
  let izlenme = 0;
  const campaignMap = {};

  for (const period of savedFormats) {
    const sections = period.sections || [];
    // "Dağılım" ile "Mecra" AYNI paranın iki kırılımı — ikisini birden toplamak harcamayı
    // (ve tüm metrikleri) çift sayar. Backend normalizer gibi Mecra-öncelikli TEK kanonik
    // tabloyu seç (Mecra yoksa Dağılım'a düş). Böylece tek-sayım + kanal kırılımı korunur.
    const isMecra = (s) => /mecra/i.test(s?.title || '');
    const isDagilim = (s) => /da[ğg]['ı i]?l/i.test(s?.title || '');
    const section = sections.find(isMecra) || sections.find(isDagilim) || sections.find(Boolean);
    if (section) {
      if (section.total) {
        butce += parseTurkishNumber(section.total.budget);
        gosterim += parseTurkishNumber(section.total.impressions);
        erisim += parseTurkishNumber(section.total.reach);
        tiklama += parseTurkishNumber(section.total.clicks);
        izlenme += parseTurkishNumber(section.total.views);
      }

      (section.rows || []).forEach((r) => {
        const key = (r.channel || '').trim();
        if (!key) return;
        if (!campaignMap[key]) {
          campaignMap[key] = { label: key, data: { butce: 0, gosterim: 0, erisim: 0, izlenme: 0, trafik: 0, tiklama: 0 } };
        }
        campaignMap[key].data.butce += parseTurkishNumber(r.budget);
        campaignMap[key].data.gosterim += parseTurkishNumber(r.impressions);
        campaignMap[key].data.erisim += parseTurkishNumber(r.reach);
        campaignMap[key].data.izlenme += parseTurkishNumber(r.views);
        campaignMap[key].data.tiklama += parseTurkishNumber(r.clicks);
      });
    }
  }

  const hasRows = Object.keys(campaignMap).length > 0;
  const totalsFromSections =
    butce !== 0 || gosterim !== 0 || erisim !== 0 || izlenme !== 0 || tiklama !== 0;

  /** Toplam satırı yoksa / sıfırsa üst satır metriklerini kanal satırlarından türet */
  if (hasRows && !totalsFromSections) {
    Object.values(campaignMap).forEach((c) => {
      butce += c.data.butce || 0;
      gosterim += c.data.gosterim || 0;
      erisim += c.data.erisim || 0;
      izlenme += c.data.izlenme || 0;
      tiklama += c.data.tiklama || 0;
    });
  }

  if (!hasRows && butce === 0 && gosterim === 0 && erisim === 0 && izlenme === 0 && tiklama === 0) {
    return null;
  }

  return {
    key: 'medya_satinalma',
    label: 'Medya Satınalma',
    isManual: false,
    data: { butce, gosterim, erisim, izlenme, trafik: 0, tiklama },
    campaigns: Object.values(campaignMap).sort((a, b) => (b.data.butce || 0) - (a.data.butce || 0)),
  };
}

/* ── Veri işleme ─────────────────────────────────────────────────────── */

// Müşteriler kampanyayı sonradan yeniden adlandırabiliyor; günlük satırlarda eski isim kalır.
// Aynı campaign_id'nin EN SON (max date) ismini bul → tüm günleri son isimle birleştir.
// id/date yoksa relabel yapılmaz (eski davranış). Branding/performans sınıflandırması da
// böylece güncel isme göre çalışır.
function buildLatestNameMap(rawData) {
  const best = {};
  (rawData || []).forEach((r) => {
    const id = r.campaign_id ?? r.campaignId;
    const name = r.campaign_name ?? r.campaignName;
    if (id == null || id === '' || name == null) return;
    const d = String(r.date ?? r.stat_date ?? '');
    if (!best[id] || d >= best[id].d) best[id] = { d, name };
  });
  const m = {};
  for (const id in best) m[id] = best[id].name;
  return m;
}
const latestCampaignName = (map, r) => {
  const id = r.campaign_id ?? r.campaignId;
  return (id != null && map[id]) || r.campaign_name || r.campaignName || '(not set)';
};

function aggregateGoogleBranding(rawData) {
  const nameMap = buildLatestNameMap(rawData);
  const t = { butce: 0, gosterim: 0, erisim: 0, izlenme: 0, trafik: 0, tiklama: 0, gelir: 0 };
  (rawData || []).forEach(r => {
    const name = latestCampaignName(nameMap, r);
    const type = r.campaignType || r.campaign_type;
    if (!isBranding(name, type) || isYoutubeCampaign(name, type)) return;
    t.butce    += Number(r.googleAdsCost)       || 0;
    t.gosterim += Number(r.googleAdsImpression) || 0;
    t.trafik   += Number(r.sessions)            || 0;
    t.tiklama  += Number(r.googleAdsClicks)     || 0;
    t.gelir    += Number(r.googleAdsRevenue || r.revenue) || 0;
  });
  t.roas = t.butce > 0 ? t.gelir / t.butce : 0;
  return t;
}

function aggregateMetaBranding(rawData) {
  const nameMap = buildLatestNameMap(rawData);
  const t = { butce: 0, gosterim: 0, erisim: 0, izlenme: 0, trafik: 0, tiklama: 0, gelir: 0, _mr: mrNew() };
  (rawData || []).forEach(r => {
    const name = latestCampaignName(nameMap, r);
    if (!isMetaBranding(name, r.campaign_objective || r.campaignObjective)) return;
    t.butce    += Number(r.spend)                                 || 0;
    t.gosterim += Number(r.impressions)                           || 0;
    t.erisim   += Number(r.reach)                                 || 0;
    // Erişim toplanamaz — kampanya kimliği tutulur, tekil değer Meta'dan sorulur
    mrAddRow(t._mr, r, { level: 'campaign' });
    t.izlenme  += Number(r.view || r.trueplay || r.video_views || r.videoViews) || 0;
    t.trafik   += Number(r.ga4_sessions || r.ga4Sessions)         || 0;
    t.tiklama  += Number(r.clicks)                                || 0;
    t.gelir    += Number(r.conversion_value || r.conversionValue) || 0;
  });
  t.roas = t.butce > 0 ? t.gelir / t.butce : 0;
  return t;
}

/** Bir satır VIDEO (YouTube) kampanyasına mı ait? campaign_type dönmeyen eski
 *  cevaplarda o zamanki davranışı koru → VIDEO varsay. */
const isVideoTypeRow = (r) =>
  String(r?.campaign_type ?? r?.campaignType ?? 'VIDEO').toUpperCase() === 'VIDEO';

function aggregateYouTubeData(videoRows) {
  const t = { butce: 0, gosterim: 0, izlenme: 0, izlenmeOrani: 0 };
  (videoRows || []).forEach(r => {
    // YALNIZCA VIDEO kampanyaları. Endpoint 2026-07-28'den beri PMax / Demand Gen /
    // Display satırlarını da döndürüyor; onların harcama ve gösterimi ZATEN
    // "Google (YouTube hariç)" satırında sayılıyor. Buraya da eklenirse harcama
    // çift sayılır — bu yüzden onlardan sadece İZLENME alınır (aşağıdaki
    // aggregateNonVideoViews) ve kendi satırlarına yazılır.
    if (!isVideoTypeRow(r)) return;
    const imp = Number(r.impressions) || 0;
    // Bumper/non-skippable/CTV (view=0) → impression'ı izlenme say (backend effective_views).
    const vv = Number(r.video_views) || 0;
    const ev = r.effective_views != null ? Number(r.effective_views) : (vv > 0 ? vv : imp);
    t.butce    += Number(r.cost)        || 0;
    t.gosterim += imp;
    t.izlenme  += ev;
  });
  if (t.gosterim > 0) t.izlenmeOrani = (t.izlenme / t.gosterim) * 100;
  return t;
}

/** PMax / Demand Gen / Display branding kampanyalarının video izlenmesi.
 *  Bunların harcama+gösterimi "Google (YouTube hariç)" satırında olduğu için
 *  yalnızca izlenme döner ve o satıra eklenir — böylece izlenme, harcamasıyla
 *  aynı satırda görünür ve hiçbir şey iki kez sayılmaz. */
function aggregateNonVideoViews(videoRows) {
  let views = 0;
  (videoRows || []).forEach(r => {
    if (isVideoTypeRow(r)) return;
    const type = r.campaign_type || r.campaignType;
    if (!isBranding(r.campaign_name, type)) return;
    const vv = Number(r.video_views) || 0;
    views += r.effective_views != null ? Number(r.effective_views) : vv;
  });
  return views;
}

function buildPlacements(json) {
  const channelNamesMap = json.channelNames || {};

  // Video sayfasıyla aynı mantık: placement (channel ID) bazında aggregate
  let rows = [];
  if (Array.isArray(json.rawData) && json.rawData.length > 0) {
    rows = json.rawData;
  } else if (Array.isArray(json.placements) && json.placements.length > 0) {
    rows = json.placements;
  } else if (Array.isArray(json.topPlacements) && json.topPlacements.length > 0) {
    rows = json.topPlacements;
  }

  // 1) Placement bazında aggregate
  const agg = new Map();
  for (const r of rows) {
    const p = r.placement;
    if (!p) continue;
    const cur = agg.get(p) || { placement: p, gosterim: 0, izlenme: 0, butce: 0 };
    cur.gosterim += Number(r.impressions || 0);
    cur.izlenme  += Number(r.video_views || 0);
    cur.butce    += Number(r.cost || 0);
    agg.set(p, cur);
  }

  // 2) Kanal isimlerini belirle (rawData channel_title, topPlacements channel_name, sonra channelNames map)
  const titleByPlacement = new Map();
  if (Array.isArray(json.rawData)) {
    for (const r of json.rawData) {
      const p = r.placement;
      const t = (r.channel_title || '').trim();
      if (p && t && !titleByPlacement.has(p)) titleByPlacement.set(p, t);
    }
  }
  if (Array.isArray(json.topPlacements)) {
    for (const r of json.topPlacements) {
      const p = r.placement;
      const t = (r.channel_name || '').trim();
      if (p && t && !titleByPlacement.has(p)) titleByPlacement.set(p, t);
    }
  }

  return Array.from(agg.values())
    .map(p => ({
      ...p,
      name: titleByPlacement.get(p.placement) || channelNamesMap[p.placement] ||
            (p.placement?.startsWith?.('UC') ? p.placement : p.placement) || 'Bilinmiyor',
      izlenmeOrani: p.gosterim > 0 ? (p.izlenme / p.gosterim) * 100 : 0,
    }))
    .sort((a, b) => (b.butce || 0) - (a.butce || 0));
}

function buildVideoCreatives(videoRows) {
  const map = {};
  // Kampanya bazlı (Yataş grubu talebi, 13 Eki 2026) — önceden ad_group_name'e göre
  // gruplanıyordu. Yeniden adlandırılan kampanyalar son isimle tek satırda birleşir.
  const nameMap = buildLatestNameMap((videoRows || []).filter(isVideoTypeRow));
  (videoRows || []).forEach(r => {
    // Yalnızca VIDEO satırları; PMax/Demand Gen harcaması "Google (YouTube hariç)"
    // satırında sayılıyor, buraya girerlerse çift gösterilir.
    if (!isVideoTypeRow(r)) return;
    const name = latestCampaignName(nameMap, r);
    if (!map[name]) map[name] = { name, butce: 0, gosterim: 0, izlenme: 0, _imp: 0 };
    const imp = Number(r.impressions) || 0;
    const rawViews = Number(r.video_views ?? r.videoViews ?? 0) || 0;
    const effectiveViews = rawViews === 0 ? imp : rawViews;
    map[name].butce    += Number(r.cost)        || 0;
    map[name].gosterim += imp;
    map[name].izlenme  += effectiveViews;
    map[name]._imp     += imp;
  });
  return Object.values(map)
    .map(v => { v.izlenmeOrani = v._imp > 0 ? (v.izlenme / v._imp) * 100 : 0; delete v._imp; return v; })
    .sort((a, b) => b.butce - a.butce);
}

function buildGoogleBrandingCampaignRows(rawData) {
  const nameMap = buildLatestNameMap(rawData);
  const map = {};
  (rawData || []).forEach((r) => {
    const campaignName = latestCampaignName(nameMap, r);
    const campaignType = r.campaignType || r.campaign_type;
    if (!isBranding(campaignName, campaignType) || isYoutubeCampaign(campaignName, campaignType)) return;
    if (!map[campaignName]) map[campaignName] = { label: campaignName, data: { butce: 0, gosterim: 0, erisim: 0, izlenme: 0, trafik: 0, gelir: 0 } };
    map[campaignName].data.butce += Number(r.googleAdsCost || r.google_cost || 0);
    map[campaignName].data.gosterim += Number(r.googleAdsImpression || r.impressions || 0);
    map[campaignName].data.trafik += Number(r.sessions || r.ga4_sessions || 0);
    map[campaignName].data.gelir += Number(r.googleAdsRevenue || r.revenue || 0);
  });
  return Object.values(map).map(c => { c.data.roas = c.data.butce > 0 ? c.data.gelir / c.data.butce : 0; return c; }).sort((a, b) => (b.data.butce || 0) - (a.data.butce || 0));
}

function buildMetaBrandingCampaignRows(rawData) {
  const nameMap = buildLatestNameMap(rawData);
  const map = {};
  (rawData || []).forEach((r) => {
    const name = latestCampaignName(nameMap, r);
    if (!isMetaBranding(name, r.campaign_objective || r.campaignObjective)) return;
    if (!map[name]) map[name] = { label: name, data: { butce: 0, gosterim: 0, erisim: 0, izlenme: 0, trafik: 0, gelir: 0, _mr: mrNew() } };
    map[name].data.butce += Number(r.spend || 0);
    map[name].data.gosterim += Number(r.impressions || 0);
    map[name].data.erisim += Number(r.reach || 0);
    mrAddRow(map[name].data._mr, r, { level: 'campaign' });
    map[name].data.izlenme += Number(r.view || r.trueplay || r.video_views || r.videoViews || 0);
    map[name].data.trafik += Number(r.ga4_sessions || r.ga4Sessions || 0);
    map[name].data.gelir += Number(r.conversion_value || r.conversionValue || 0);
  });
  return Object.values(map).map(c => { c.data.roas = c.data.butce > 0 ? c.data.gelir / c.data.butce : 0; return c; }).sort((a, b) => (b.data.butce || 0) - (a.data.butce || 0));
}

/* ── Tablo bileşenleri ──────────────────────────────────────────────── */

function Section({ icon: Icon, iconSrc, title, subtitle, children }) {
  return (
    <div className="mb-8">
      <div className="flex items-center gap-2 mb-3">
        {iconSrc ? (
          <div className="flex items-center justify-center flex-shrink-0">
            <img src={iconSrc} alt={title} className="w-5 h-5 object-contain" />
          </div>
        ) : Icon && (
          <div className="w-7 h-7 rounded-lg bg-slate-800 flex items-center justify-center flex-shrink-0">
            <Icon size={14} className="text-slate-300" />
          </div>
        )}
        <div>
          <h2 className="text-[13px] font-bold text-slate-800 leading-tight">{title}</h2>
          {subtitle && <p className="text-[10px] text-slate-400">{subtitle}</p>}
        </div>
      </div>
      {children}
    </div>
  );
}

const PRESET_FILTERS = ['YBB', 'Renkli', 'Beyaz'];
// Enza Home'da YBB/Renkli/Beyaz kategori filtreleri yerine Branding/Performans gösterilir.
// Bu chip'ler kampanya ADINDA arama yapar, o yüzden sadece adlandırması bu kelimeleri
// içeren markalara verilir. Divanev buradan ÇIKARILDI: 30 Meta + 27 Google kampanyasının
// hiçbirinde "Branding"/"Performans" geçmiyor (`DH-[Reach]`, `DH-[Pmax]` … biçiminde),
// iki chip de her zaman boş tablo döndürüyordu.
const BRANDING_PERF_PRESET_BRAND_IDS = new Set([
  'ccb2364b-e4e1-4f7f-944a-44cc9a839c6e', // EnzaHome
]);
// Adlandırması hiçbir preset'e uymayan markalarda chip yerine serbest arama kalır.
const NO_PRESET_BRAND_IDS = new Set([
  'dfcf54dd-b00d-434a-be1e-27b6484848eb', // Divanev
]);
const getPresetFilters = (brandId) => {
  if (NO_PRESET_BRAND_IDS.has(brandId)) return [];
  return BRANDING_PERF_PRESET_BRAND_IDS.has(brandId) ? ['Branding', 'Performans'] : PRESET_FILTERS;
};

function DataTable({ label, columns, rows, totalRow, maxRows, stickyFooter = false, presetFilters = PRESET_FILTERS }) {
  const [filterTags, setFilterTags] = useState([]);
  const [filterInput, setFilterInput] = useState('');

  const addTag = useCallback((val) => {
    const v = (val || '').trim();
    if (!v) return;
    setFilterTags(prev => prev.includes(v) ? prev : [...prev, v]);
  }, []);
  const removeTag = useCallback((idx) => {
    setFilterTags(prev => prev.filter((_, i) => i !== idx));
  }, []);
  const togglePreset = useCallback((val) => {
    setFilterTags(prev => prev.includes(val) ? prev.filter(t => t !== val) : [...prev, val]);
  }, []);

  const filteredRows = useMemo(() => {
    if (filterTags.length === 0) return rows;
    const lowerTags = filterTags.map(t => t.toLowerCase());
    return rows.filter(row => {
      const name = (row.label || '').toLowerCase();
      return lowerTags.some(tag => name.includes(tag));
    });
  }, [rows, filterTags]);

  const displayRows = maxRows ? filteredRows.slice(0, maxRows) : filteredRows;

  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <div className="relative flex-1 max-w-lg flex items-center flex-wrap gap-1.5 border border-slate-200 rounded-md bg-white px-2 py-1.5 focus-within:ring-2 focus-within:ring-blue-500/30 focus-within:border-blue-400 shadow-sm min-h-[32px]">
          <Search size={13} className="text-slate-400 flex-shrink-0" />
          {filterTags.map((tag, idx) => (
            <span key={idx} className="inline-flex items-center gap-1 bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full text-[10px] font-medium">
              {tag}
              <button onClick={() => removeTag(idx)} className="hover:text-red-500 transition-colors font-bold text-[10px] leading-none">
                <X size={10} />
              </button>
            </span>
          ))}
          <input
            type="text"
            value={filterInput}
            onChange={e => setFilterInput(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addTag(e.target.value);
                setFilterInput('');
              }
              if (e.key === 'Backspace' && !e.target.value && filterTags.length > 0) {
                removeTag(filterTags.length - 1);
              }
            }}
            placeholder={filterTags.length === 0 ? 'Kanal adı yaz, Enter ile filtre ekle… (OR)' : 'Filtre ekle…'}
            className="flex-1 min-w-[120px] text-[11px] bg-transparent text-slate-700 placeholder-slate-400 focus:outline-none border-none p-0"
          />
        </div>
        {filterTags.length > 0 && (
          <button onClick={() => setFilterTags([])} className="text-[10px] text-slate-400 hover:text-red-500 transition-colors whitespace-nowrap">
            Temizle
          </button>
        )}
        <div className="flex items-center gap-1 ml-2">
          {presetFilters.map(pf => (
            <button
              key={pf}
              onClick={() => togglePreset(pf)}
              className={`px-2 py-0.5 rounded-full text-[10px] font-medium border transition-colors ${
                filterTags.includes(pf)
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'bg-white text-slate-500 border-slate-300 hover:border-blue-400 hover:text-blue-600'
              }`}
            >
              {pf}
            </button>
          ))}
        </div>
      </div>

      <div className="rounded-lg border border-slate-200/80 overflow-hidden bg-white">
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-gradient-to-r from-slate-800 to-slate-700">
                <th className="sticky left-0 z-20 bg-slate-800 px-3 py-2.5 text-left text-[10px] font-medium text-slate-400 uppercase tracking-wider" style={{ minWidth: 180 }}>
                  {label}
                </th>
                {columns.map(c => (
                  <th key={c.key} className="px-3 py-2.5 text-right text-[10px] font-medium text-slate-400 uppercase tracking-wider whitespace-nowrap" style={{ minWidth: 100 }}>
                    {c.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {displayRows.length === 0 && (
                <tr>
                  <td colSpan={columns.length + 1} className="px-3 py-6 text-center text-[11px] text-slate-400">
                    {filterTags.length > 0 ? 'Filtre ile eşleşen kanal bulunamadı' : 'Veri bulunamadı'}
                  </td>
                </tr>
              )}
              {displayRows.map((row, i) => (
                <tr key={row.label || i} className={`transition-colors duration-100 ${i % 2 === 0 ? 'bg-white' : 'bg-slate-50/40'} hover:bg-blue-50/40`}>
                  <td className="sticky left-0 z-10 px-3 py-2 text-[11px] text-slate-700 border-b border-slate-100/80 whitespace-nowrap font-medium" style={{ backgroundColor: 'inherit' }}>
                    <div className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-slate-300 flex-shrink-0" />
                      {row.label}
                    </div>
                  </td>
                  {columns.map(c => (
                    <td key={c.key} className={`px-3 py-2 text-right text-[11px] tabular-nums border-b border-slate-100/80 whitespace-nowrap ${row.data?.[c.key] != null ? 'text-slate-700' : 'text-slate-300'}`}>
                      {fmt(row.data?.[c.key], c.format)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
            {totalRow && Object.keys(totalRow).length > 0 && (
              <tfoot>
                <tr className={`bg-gradient-to-r from-slate-900 to-slate-800 ${stickyFooter ? 'sticky bottom-0 z-30' : ''}`}>
                  <td className="sticky left-0 z-10 bg-slate-900 px-3 py-2.5 text-[11px] font-bold text-white uppercase tracking-wide">
                    Toplam
                  </td>
                  {columns.map(c => (
                    <td key={c.key} className="px-3 py-2.5 text-right text-[11px] font-bold text-white tabular-nums whitespace-nowrap">
                      {fmt(totalRow[c.key], c.format)}
                    </td>
                  ))}
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </div>
  );
}

function BrandingChannelsTable({ columns, rows, totalRow, expandedRows, onToggle, presetFilters = PRESET_FILTERS }) {
  const reachOf = useReachOf();
  const [filterTags, setFilterTags] = useState([]);
  const [filterInput, setFilterInput] = useState('');

  const addTag = useCallback((val) => {
    const v = (val || '').trim();
    if (!v) return;
    setFilterTags(prev => prev.includes(v) ? prev : [...prev, v]);
  }, []);
  const removeTag = useCallback((idx) => {
    setFilterTags(prev => prev.filter((_, i) => i !== idx));
  }, []);
  const togglePreset = useCallback((val) => {
    setFilterTags(prev => prev.includes(val) ? prev.filter(t => t !== val) : [...prev, val]);
  }, []);

  const filteredRows = useMemo(() => {
    if (filterTags.length === 0) return rows;
    const lowerTags = filterTags.map(t => t.toLowerCase());
    return rows.map(row => {
      const filtered = (row.campaigns || []).filter(c => {
        const name = (c.label || '').toLowerCase();
        return lowerTags.some(tag => name.includes(tag));
      });
      if (filtered.length === 0) return null;
      // Erişim: Meta kampanyalarının kümesi birleştirilip tekil sorulur
      const data = sumMetrics(filtered.map(c => c.data), columns, reachOf);
      if (data.butce > 0 && data.gelir != null) data.roas = data.gelir / data.butce;
      if (data.harcama > 0 && data.gelir != null) data.roas = data.gelir / data.harcama;
      return { ...row, data, campaigns: filtered };
    }).filter(Boolean);
  }, [rows, filterTags, columns, reachOf]);

  const filteredTotal = useMemo(() => {
    if (filterTags.length === 0) return totalRow;
    const t = sumMetrics(filteredRows.map(r => r.data), columns, reachOf);
    if (t.butce > 0 && t.gelir != null) t.roas = t.gelir / t.butce;
    if (t.harcama > 0 && t.gelir != null) t.roas = t.gelir / t.harcama;
    return t;
  }, [filteredRows, filterTags, totalRow, columns, reachOf]);

  const displayRows = filteredRows;
  const displayTotal = filteredTotal;

  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <div className="relative flex-1 max-w-lg flex items-center flex-wrap gap-1.5 border border-slate-200 rounded-md bg-white px-2 py-1.5 focus-within:ring-2 focus-within:ring-blue-500/30 focus-within:border-blue-400 shadow-sm min-h-[32px]">
          <Search size={13} className="text-slate-400 flex-shrink-0" />
          {filterTags.map((tag, idx) => (
            <span key={idx} className="inline-flex items-center gap-1 bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full text-[10px] font-medium">
              {tag}
              <button onClick={() => removeTag(idx)} className="hover:text-red-500 transition-colors font-bold text-[10px] leading-none">
                <X size={10} />
              </button>
            </span>
          ))}
          <input
            type="text"
            value={filterInput}
            onChange={e => setFilterInput(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addTag(e.target.value);
                setFilterInput('');
              }
              if (e.key === 'Backspace' && !e.target.value && filterTags.length > 0) {
                removeTag(filterTags.length - 1);
              }
            }}
            placeholder={filterTags.length === 0 ? 'Kampanya adı yaz, Enter ile filtre ekle… (OR)' : 'Filtre ekle…'}
            className="flex-1 min-w-[120px] text-[11px] bg-transparent text-slate-700 placeholder-slate-400 focus:outline-none border-none p-0"
          />
        </div>
        {filterTags.length > 0 && (
          <button onClick={() => setFilterTags([])} className="text-[10px] text-slate-400 hover:text-red-500 transition-colors whitespace-nowrap">
            Temizle
          </button>
        )}
        <div className="flex items-center gap-1 ml-2">
          {presetFilters.map(pf => (
            <button
              key={pf}
              onClick={() => togglePreset(pf)}
              className={`px-2 py-0.5 rounded-full text-[10px] font-medium border transition-colors ${
                filterTags.includes(pf)
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'bg-white text-slate-500 border-slate-300 hover:border-blue-400 hover:text-blue-600'
              }`}
            >
              {pf}
            </button>
          ))}
        </div>
      </div>

      <div className="rounded-lg border border-slate-200/80 overflow-hidden bg-white">
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-gradient-to-r from-slate-800 to-slate-700">
                <th className="sticky left-0 z-20 bg-slate-800 px-3 py-2.5 text-left text-[10px] font-medium text-slate-400 uppercase tracking-wider" style={{ minWidth: 220 }}>
                  Kanal
                </th>
                {columns.map(c => (
                  <th key={c.key} className="px-3 py-2.5 text-right text-[10px] font-medium text-slate-400 uppercase tracking-wider whitespace-nowrap" style={{ minWidth: 100 }}>
                    {c.label}
                  </th>
                ))}
              </tr>
            </thead>
            {displayTotal && Object.keys(displayTotal).length > 0 && (
              <thead>
                <tr className="bg-gradient-to-r from-slate-900 to-slate-800">
                  <th className="sticky left-0 z-20 bg-slate-900 px-3 py-2.5 text-left text-[11px] font-bold text-white uppercase tracking-wide" style={{ minWidth: 220 }}>Toplam</th>
                  {columns.map(c => (
                    <th key={`top-total-${c.key}`} className="px-3 py-2.5 text-right text-[11px] font-bold text-white tabular-nums whitespace-nowrap" style={{ minWidth: 100 }}>{fmt(displayTotal[c.key], c.format)}</th>
                  ))}
                </tr>
              </thead>
            )}
            <tbody>
              {displayRows.length === 0 && (
                <tr>
                  <td colSpan={columns.length + 1} className="px-3 py-6 text-center text-[11px] text-slate-400">
                    {filterTags.length > 0 ? 'Filtre ile eşleşen kampanya bulunamadı' : 'Veri bulunamadı'}
                  </td>
                </tr>
              )}
              {displayRows.map((row, i) => {
                const isOpen = !!expandedRows[row.label];
                return (
                  <React.Fragment key={row.label}>
                    <tr className={`transition-colors duration-100 ${i % 2 === 0 ? 'bg-white' : 'bg-slate-50/40'} hover:bg-blue-50/40`}>
                      <td className="sticky left-0 z-10 px-3 py-2 text-[11px] text-slate-700 border-b border-slate-100/80 whitespace-nowrap font-medium" style={{ backgroundColor: 'inherit' }}>
                        <button type="button" onClick={() => onToggle(row.label)} className="w-full flex items-center gap-2 text-left">
                          {isOpen ? <ChevronDown size={13} className="text-slate-500 flex-shrink-0" /> : <ChevronRight size={13} className="text-slate-500 flex-shrink-0" />}
                          <span className="w-1.5 h-1.5 rounded-full bg-slate-300 flex-shrink-0" />
                          <span>{row.label}</span>
                          {filterTags.length > 0 && <span className="text-[9px] text-slate-400 font-normal">({(row.campaigns || []).length})</span>}
                        </button>
                      </td>
                      {columns.map(c => (
                        <td key={c.key} className={`px-3 py-2 text-right text-[11px] tabular-nums border-b border-slate-100/80 whitespace-nowrap ${row.data?.[c.key] != null ? 'text-slate-700' : 'text-slate-300'}`}>
                          {fmt(row.data?.[c.key], c.format)}
                        </td>
                      ))}
                    </tr>
                    {isOpen && (
                      <tr className={`${i % 2 === 0 ? 'bg-slate-50/30' : 'bg-white'}`}>
                        <td colSpan={columns.length + 1} className="px-4 py-3 border-b border-slate-100/80">
                          <div className="rounded-md border border-slate-200/80 overflow-hidden">
                            <table className="w-full border-collapse">
                              <thead>
                                <tr className="bg-slate-100/80">
                                  <th className="px-3 py-1.5 text-left text-[9px] font-medium text-slate-500 uppercase tracking-wider">Kampanya</th>
                                  {columns.map(c => (
                                    <th key={c.key} className="px-3 py-1.5 text-right text-[9px] font-medium text-slate-500 uppercase tracking-wider">{c.label}</th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {(row.campaigns || []).length === 0 && (
                                  <tr>
                                    <td colSpan={columns.length + 1} className="px-3 py-3 text-center text-[10px] text-slate-400">Kampanya verisi bulunamadı</td>
                                  </tr>
                                )}
                                {(row.campaigns || []).map((cRow, idx2) => (
                                  <tr key={`${row.label}-${cRow.label}-${idx2}`} className={idx2 % 2 === 0 ? 'bg-white' : 'bg-slate-50/30'}>
                                    <td className="px-3 py-1.5 text-[10px] text-slate-700 border-b border-slate-100/70">{cRow.label}</td>
                                    {columns.map(c => (
                                      <td key={`${cRow.label}-${c.key}`} className="px-3 py-1.5 text-right text-[10px] text-slate-700 tabular-nums border-b border-slate-100/70">
                                        {fmt(cRow.data?.[c.key], c.format)}
                                      </td>
                                    ))}
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
            {displayTotal && Object.keys(displayTotal).length > 0 && (
              <tfoot>
                <tr className="bg-gradient-to-r from-slate-900 to-slate-800">
                  <td className="sticky left-0 z-10 bg-slate-900 px-3 py-2.5 text-[11px] font-bold text-white uppercase tracking-wide">Toplam</td>
                  {columns.map(c => (
                    <td key={c.key} className="px-3 py-2.5 text-right text-[11px] font-bold text-white tabular-nums whitespace-nowrap">{fmt(displayTotal[c.key], c.format)}</td>
                  ))}
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </div>
  );
}

function SavedMediaBuyingTable({ title, rows, total }) {
  return (
    <div className="rounded-md border border-slate-200/80 overflow-hidden bg-white">
      <div className="bg-slate-50 px-3 py-2 border-b border-slate-200/80">
        <h4 className="text-[11px] font-semibold text-slate-700">{title}</h4>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-slate-100/70">
              <th className="px-3 py-2 text-left text-[10px] font-medium text-slate-500 uppercase tracking-wider">Kanal</th>
              <th className="px-3 py-2 text-left text-[10px] font-medium text-slate-500 uppercase tracking-wider">Kampanya Adı</th>
              <th className="px-3 py-2 text-right text-[10px] font-medium text-slate-500 uppercase tracking-wider">Bütçe</th>
              <th className="px-3 py-2 text-right text-[10px] font-medium text-slate-500 uppercase tracking-wider">Gösterim</th>
              <th className="px-3 py-2 text-right text-[10px] font-medium text-slate-500 uppercase tracking-wider">Tıklama</th>
              <th className="px-3 py-2 text-right text-[10px] font-medium text-slate-500 uppercase tracking-wider">İzlenme</th>
            </tr>
          </thead>
          <tbody>
            {!rows?.length && (
              <tr>
                <td colSpan={6} className="px-3 py-4 text-center text-[11px] text-slate-400">Veri bulunamadı</td>
              </tr>
            )}
            {(rows || []).map((r, idx) => (
              <tr key={`${title}-row-${idx}`} className={idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/40'}>
                <td className="px-3 py-2 text-[11px] text-slate-700 border-b border-slate-100/80 whitespace-nowrap">{r.channel || '–'}</td>
                <td className="px-3 py-2 text-[11px] text-slate-700 border-b border-slate-100/80 whitespace-nowrap">{r.campaign || '–'}</td>
                <td className="px-3 py-2 text-right text-[11px] text-slate-700 border-b border-slate-100/80 tabular-nums whitespace-nowrap">{r.budget || '–'}</td>
                <td className="px-3 py-2 text-right text-[11px] text-slate-700 border-b border-slate-100/80 tabular-nums whitespace-nowrap">{r.impressions || '–'}</td>
                <td className="px-3 py-2 text-right text-[11px] text-slate-700 border-b border-slate-100/80 tabular-nums whitespace-nowrap">{r.clicks || '–'}</td>
                <td className="px-3 py-2 text-right text-[11px] text-slate-700 border-b border-slate-100/80 tabular-nums whitespace-nowrap">{r.views || '–'}</td>
              </tr>
            ))}
          </tbody>
          {total && (
            <tfoot>
              <tr className="bg-gradient-to-r from-slate-900 to-slate-800">
                <td className="px-3 py-2 text-[11px] font-bold text-white uppercase tracking-wide">{total.channel || 'Toplam'}</td>
                <td className="px-3 py-2 text-[11px] font-bold text-white">{total.campaign || ''}</td>
                <td className="px-3 py-2 text-right text-[11px] font-bold text-white tabular-nums">{total.budget || '–'}</td>
                <td className="px-3 py-2 text-right text-[11px] font-bold text-white tabular-nums">{total.impressions || '–'}</td>
                <td className="px-3 py-2 text-right text-[11px] font-bold text-white tabular-nums">{total.clicks || '–'}</td>
                <td className="px-3 py-2 text-right text-[11px] font-bold text-white tabular-nums">{total.views || '–'}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}

/* ── KPI Kartları ────────────────────────────────────────────────────── */

function KpiCards({ brandingTotal }) {
  const total = brandingTotal || {};

  const cards = [
    { label: 'Toplam Branding Bütçe', value: fmt(total.butce, 'currency'), sub: 'Tüm branding kanalları' },
    { label: 'Toplam Gösterim', value: fmt(total.gosterim, 'number'), sub: 'Tüm branding kanalları' },
    { label: 'Toplam İzlenme', value: fmt(total.izlenme, 'number'), sub: 'Video views (tüm kanallar)' },
    { label: 'Toplam Trafik', value: fmt(total.trafik, 'number'), sub: 'Sessions (tüm kanallar)' },
  ];

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-8">
      {cards.map((c, i) => (
        <div key={i} className="bg-white rounded-lg border border-slate-200/80 px-4 py-3">
          <p className="text-[10px] text-slate-400 uppercase tracking-wider font-medium">{c.label}</p>
          <p className="text-lg font-bold text-slate-800 mt-0.5 tabular-nums">{c.value}</p>
          <p className="text-[10px] text-slate-400 mt-0.5">{c.sub}</p>
        </div>
      ))}
    </div>
  );
}

/* ── Ana bileşen ────────────────────────────────────────────────────── */

export default function CustomReport2() {
  // Her render'da okunur — useMemo(..., []) ile sabitlenirse sayfa açıkken marka
  // değiştirildiğinde chip'ler ilk markanınkinde kalıyordu.
  const presetFilters = getPresetFilters(localStorage.getItem('currentBrandId'));
  const [startDate, setStartDate] = useState(() => getLastCompletedMonthRange().startDate);
  const [endDate, setEndDate] = useState(() => getLastCompletedMonthRange().endDate);
  // Meta erişimi: DB toplamı yerine Meta API'den tekil değer (utils/metaReachDedup)
  const reachOf = useMetaReach(API, localStorage.getItem('currentBrandId'), startDate, endDate);
  const [pendingStartDate, setPendingStartDate] = useState(() => getLastCompletedMonthRange().startDate);
  const [pendingEndDate, setPendingEndDate] = useState(() => getLastCompletedMonthRange().endDate);
  const initialAutoCompare = useMemo(() => {
    const base = getLastCompletedMonthRange();
    return getComparisonPeriods(base.startDate, base.endDate);
  }, []);
  const [compareStartDate, setCompareStartDate] = useState(() => initialAutoCompare.prevStart);
  const [compareEndDate, setCompareEndDate] = useState(() => initialAutoCompare.prevEnd);
  const [pendingCompareStartDate, setPendingCompareStartDate] = useState(() => initialAutoCompare.prevStart);
  const [pendingCompareEndDate, setPendingCompareEndDate] = useState(() => initialAutoCompare.prevEnd);
  const [isCompareAuto, setIsCompareAuto] = useState(true);
  const [showComparePicker, setShowComparePicker] = useState(false);
  const [loading, setLoading] = useState(false);
  const pdfRef = useRef(null);
  const [exporting, setExporting] = useState(false);

  const handleExportPDF = useCallback(async () => {
    if (exporting || !pdfRef.current) return;
    setExporting(true);
    try {
      const el = pdfRef.current;
      const canvas = await html2canvas(el, { scale: 1.5, useCORS: true, logging: false, backgroundColor: '#ffffff', windowWidth: 1400, letterRendering: true, allowTaint: true });
      const pdf = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a3' });
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const margin = 8;
      const usableW = pageW - margin * 2;
      const usableH = pageH - margin * 2;
      const imgW = usableW;
      const imgH = (canvas.height * usableW) / canvas.width;
      let y = 0;
      let page = 0;
      while (y < imgH) {
        if (page > 0) pdf.addPage();
        const srcY = (y / imgH) * canvas.height;
        const srcH = Math.min((usableH / imgH) * canvas.height, canvas.height - srcY);
        const sliceCanvas = document.createElement('canvas');
        sliceCanvas.width = canvas.width;
        sliceCanvas.height = srcH;
        sliceCanvas.getContext('2d').drawImage(canvas, 0, srcY, canvas.width, srcH, 0, 0, canvas.width, srcH);
        const sliceData = sliceCanvas.toDataURL('image/jpeg', 0.92);
        const sliceH = (srcH * usableW) / canvas.width;
        pdf.addImage(sliceData, 'JPEG', margin, margin, imgW, sliceH);
        y += usableH;
        page++;
      }
      pdf.save('Branding_Performans_Raporu.pdf');
    } catch (err) { console.error('PDF export failed:', err); }
    finally { setExporting(false); }
  }, [exporting]);

  const [googleBranding, setGoogleBranding] = useState(null);
  const [metaBranding, setMetaBranding] = useState(null);
  const [googleBrandingCampaigns, setGoogleBrandingCampaigns] = useState([]);
  const [metaBrandingCampaigns, setMetaBrandingCampaigns] = useState([]);
  const [manualBrandingRows, setManualBrandingRows] = useState([]);
  const [monthlyBrandingBudget, setMonthlyBrandingBudget] = useState(
    MONTH_LABELS_TR.map((month) => ({ month, amount: 0, year: TARGET_PLANNER_YEAR }))
  );
  const [savedMediaBuyingFormats, setSavedMediaBuyingFormats] = useState([]);
  const [loadingSavedMediaBuying, setLoadingSavedMediaBuying] = useState(false);
  const [expandedBrandingRows, setExpandedBrandingRows] = useState({});
  const [expandedYoutubeRows, setExpandedYoutubeRows] = useState({});
  const [visiblePlacementCount, setVisiblePlacementCount] = useState(15);
  const [youtubeTotal, setYoutubeTotal] = useState(null);
  // PMax/Demand Gen/Display izlenmesi — "Google (YouTube hariç)" satırına eklenir
  const [nonVideoViews, setNonVideoViews] = useState(0);
  const [ytPlacements, setYtPlacements] = useState([]);
  const [ytCreatives, setYtCreatives] = useState([]);
  const [adGroupVideos, setAdGroupVideos] = useState({});

  const [pinterestRow, setPinterestRow] = useState(null);
  const [tiktokRow, setTiktokRow] = useState(null);

  const qp = useCallback(() => new URLSearchParams({
    dateRange: 'custom', startDate: fmtDate(startDate), endDate: fmtDate(endDate),
  }), [startDate, endDate]);

  /* ── Fetch ─────────────────────────────────────────────────────────── */

  const fetchGoogleBranding = useCallback(async () => {
    try {
      const brandId = localStorage.getItem('currentBrandId');
      if (!brandId) return;
      const res = await fetch(`${API}/api/google-ads/campaign-types-all?${qp()}`, { credentials: 'include', headers: hdr(brandId) });
      if (!res.ok) return;
      const json = await res.json();
      const raw = haricleriAyikla(Array.isArray(json?.rawData) ? json.rawData : [], brandId);
      setGoogleBranding(aggregateGoogleBranding(raw));
      setGoogleBrandingCampaigns(buildGoogleBrandingCampaignRows(raw));
    } catch (e) { console.error('Google branding:', e); }
  }, [qp]);

  const fetchMetaBranding = useCallback(async () => {
    try {
      const brandId = localStorage.getItem('currentBrandId');
      if (!brandId) return;
      const res = await fetch(`${API}/api/google-ads/unified-meta-ga4-performance?${qp()}`, { credentials: 'include', headers: hdr(brandId) });
      if (!res.ok) return;
      const json = await res.json();
      const arr = haricleriAyikla(Array.isArray(json) ? json : json.data || json.rawData || [], brandId);
      setMetaBranding(aggregateMetaBranding(arr));
      setMetaBrandingCampaigns(buildMetaBrandingCampaignRows(arr));
    } catch (e) { console.error('Meta branding:', e); }
  }, [qp]);

  const fetchYouTubeData = useCallback(async () => {
    try {
      const brandId = localStorage.getItem('currentBrandId');
      if (!brandId) return;
      const res = await fetch(`${API}/api/google-ads/video-performance-summary?${qp()}`, { credentials: 'include', headers: hdr(brandId) });
      if (!res.ok) return;
      const rows = await res.json();
      const arr = haricleriAyikla(Array.isArray(rows) ? rows : rows.data || rows.rawData || [], brandId);
      setYoutubeTotal(aggregateYouTubeData(arr));
      setNonVideoViews(aggregateNonVideoViews(arr));
      setYtCreatives(buildVideoCreatives(arr));
    } catch (e) { console.error('YouTube data:', e); }
  }, [qp]);

  const fetchYouTubePlacements = useCallback(async () => {
    try {
      const brandId = localStorage.getItem('currentBrandId');
      if (!brandId) return;
      const params = new URLSearchParams({ ...Object.fromEntries(qp()), fetchChannelNames: 'true', topPlacementsOnly: 'true' });
      const res = await fetch(`${API}/api/google-ads/youtube-placement-raw?${params}`, { credentials: 'include', headers: hdr(brandId) });
      if (!res.ok) {
        console.warn('YouTube placements endpoint returned', res.status);
        return;
      }
      const json = await res.json();
      setYtPlacements(buildPlacements(json));
    } catch (e) { console.error('YouTube placements:', e); }
  }, [qp]);

  const fetchPinterestData = useCallback(async () => {
    try {
      const brandId = localStorage.getItem('currentBrandId');
      if (!brandId) return;
      const params = new URLSearchParams({ startDate: fmtDate(startDate), endDate: fmtDate(endDate) });
      const res = await fetch(`${API}/api/pinterest/db/stats?${params}`, { credentials: 'include', headers: hdr(brandId) });
      if (!res.ok) { setPinterestRow(null); return; }
      const json = await res.json();
      const t = json.totals;
      if (!t || (t.spend === 0 && t.impressions === 0)) { setPinterestRow(null); return; }

      const campaigns = (json.campaigns || []).map(c => ({
        label: c.name || c.id || 'Unknown',
        data: {
          butce: c.spend || 0,
          gosterim: c.impressions || 0,
          erisim: c.reach || 0,
          izlenme: c.video_views || 0,
          trafik: c.ga4_sessions || 0,
          tiklama: c.clicks || 0,
        },
      }));

      setPinterestRow({
        key: 'pinterest',
        label: 'Pinterest',
        isManual: false,
        data: {
          butce: t.spend || 0,
          gosterim: t.impressions || 0,
          erisim: t.reach || 0,
          izlenme: t.video_views || 0,
          trafik: t.sessions || 0,
          tiklama: t.clicks || 0,
        },
        campaigns,
      });
    } catch (e) {
      console.error('Pinterest data:', e);
      setPinterestRow(null);
    }
  }, [startDate, endDate]);

  const fetchTikTokData = useCallback(async () => {
    try {
      const brandId = localStorage.getItem('currentBrandId');
      if (!brandId) return;
      const params = new URLSearchParams({ startDate: fmtDate(startDate), endDate: fmtDate(endDate) });
      const res = await fetch(`${API}/api/tiktok-ads/unified-campaign-performance?${params}`, { credentials: 'include', headers: hdr(brandId) });
      if (!res.ok) { setTiktokRow(null); return; }
      const rows = await res.json();
      if (!Array.isArray(rows)) { setTiktokRow(null); return; }
      const t = tiktokBrandingTotals(rows, brandId);
      if (t.butce === 0 && t.gosterim === 0) { setTiktokRow(null); return; }
      setTiktokRow({
        key: 'tiktok',
        label: 'TikTok',
        isManual: false,
        data: t,
        campaigns: [],
      });
    } catch (e) {
      console.error('TikTok data:', e);
      setTiktokRow(null);
    }
  }, [startDate, endDate]);

  const fetchAdGroupVideos = useCallback(async () => {
    try {
      const brandId = localStorage.getItem('currentBrandId');
      if (!brandId) return;
      const res = await fetch(`${API}/api/google-ads/youtube-campaigns-live?${qp()}`, { credentials: 'include', headers: hdr(brandId) });
      if (!res.ok) return;
      const data = await res.json();
      if (data?.adGroupVideos) setAdGroupVideos(data.adGroupVideos);
    } catch (e) { console.error('Ad group videos:', e); }
  }, [qp]);

  const fetchManualBrandingTargets = useCallback(async () => {
    try {
      const brandId = localStorage.getItem('currentBrandId');
      if (!brandId) return;

      const years = Array.from(new Set([startDate.getFullYear(), endDate.getFullYear()]));
      const responses = await Promise.all(
        years.map(async (year) => {
          const res = await fetch(`${API}/api/brands/${brandId}/monthly-target-plans?year=${year}`, {
            credentials: 'include',
            headers: hdr(brandId),
          });
          if (!res.ok) return { year, rows: [] };
          const json = await res.json();
          return { year, rows: json?.rows || [] };
        })
      );

      const baseRows = buildManualBrandingRowsFromPlans(responses, startDate, endDate);
      setManualBrandingRows(baseRows);
    } catch (e) {
      console.error('Manual branding targets:', e);
      setManualBrandingRows([]);
    }
  }, [startDate, endDate]);

  const [monthlyActualCosts, setMonthlyActualCosts] = useState(() => ({}));

  const fetch2026BrandingBudget = useCallback(async () => {
    try {
      const brandId = localStorage.getItem('currentBrandId');
      if (!brandId) return;
      const res = await fetch(`${API}/api/brands/${brandId}/monthly-target-plans?year=${TARGET_PLANNER_YEAR}`, {
        credentials: 'include',
        headers: hdr(brandId),
      });
      if (!res.ok) {
        setMonthlyBrandingBudget(MONTH_LABELS_TR.map((month) => ({ month, amount: 0, year: TARGET_PLANNER_YEAR })));
        return;
      }
      const json = await res.json();
      setMonthlyBrandingBudget(buildMonthlyBrandingBudgetFromYearRows(json?.rows || [], TARGET_PLANNER_YEAR));
    } catch (e) {
      console.error('2026 branding budget:', e);
      setMonthlyBrandingBudget(MONTH_LABELS_TR.map((month) => ({ month, amount: 0, year: TARGET_PLANNER_YEAR })));
    }
  }, []);

  const fetchMonthlyActualBrandingCosts = useCallback(async () => {
    const brandId = localStorage.getItem('currentBrandId');
    if (!brandId) return;
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth() + 1;
    const completedMonths = [];
    for (let m = 1; m <= 12; m++) {
      if (currentYear > TARGET_PLANNER_YEAR || (currentYear === TARGET_PLANNER_YEAR && m < currentMonth)) {
        completedMonths.push(m);
      }
    }
    if (completedMonths.length === 0) {
      setMonthlyActualCosts({});
      return;
    }
    const actuals = {};
    await Promise.all(completedMonths.map(async (month) => {
      const monthStart = new Date(TARGET_PLANNER_YEAR, month - 1, 1);
      const monthEnd = new Date(TARGET_PLANNER_YEAR, month, 0, 23, 59, 59, 999);
      const params = `dateRange=custom&startDate=${fmtDate(monthStart)}&endDate=${fmtDate(monthEnd)}`;
      let cost = 0;
      try {
        const [gRes, mRes, yRes, pRes, mbRes, tRes] = await Promise.all([
          fetch(`${API}/api/google-ads/campaign-types-all?${params}`, { credentials: 'include', headers: hdr(brandId) }),
          fetch(`${API}/api/google-ads/unified-meta-ga4-performance?${params}`, { credentials: 'include', headers: hdr(brandId) }),
          fetch(`${API}/api/google-ads/video-performance-summary?${params}`, { credentials: 'include', headers: hdr(brandId) }),
          fetch(`${API}/api/pinterest/db/stats?startDate=${fmtDate(monthStart)}&endDate=${fmtDate(monthEnd)}`, { credentials: 'include', headers: hdr(brandId) }),
          fetch(`${API}/api/brands/${brandId}/media-buying-format?year=${TARGET_PLANNER_YEAR}&month=${month}`, { credentials: 'include', headers: hdr(brandId) }),
          fetch(`${API}/api/tiktok-ads/unified-campaign-performance?startDate=${fmtDate(monthStart)}&endDate=${fmtDate(monthEnd)}`, { credentials: 'include', headers: hdr(brandId) }),
        ]);
        const gJson = await gRes.json();
        const gRaw = haricleriAyikla(Array.isArray(gJson?.rawData) ? gJson.rawData : [], brandId);
        cost += aggregateGoogleBranding(gRaw)?.butce || 0;
        const mJson = await mRes.json();
        const mArr = haricleriAyikla(Array.isArray(mJson) ? mJson : mJson?.data || mJson?.rawData || [], brandId);
        cost += aggregateMetaBranding(mArr)?.butce || 0;
        const yJson = await yRes.json();
        const yArr = haricleriAyikla(Array.isArray(yJson) ? yJson : yJson?.data || yJson?.rawData || [], brandId);
        cost += aggregateYouTubeData(yArr)?.butce || 0;
        const pJson = await pRes.json();
        if (pJson?.totals?.spend) cost += Number(pJson.totals.spend) || 0;
        const mbJson = await mbRes.json();
        const secs = mbJson?.sections || [];
        let mbBudget = 0;
        secs.forEach((sec) => {
          const b = parseTurkishNumber(sec?.total?.budget);
          if (b > mbBudget) mbBudget = b;
        });
        if (mbBudget === 0) {
          let rowSum = 0;
          secs.forEach((sec) => {
            (sec?.rows || []).forEach((r) => {
              rowSum += parseTurkishNumber(r.budget);
            });
          });
          mbBudget = rowSum;
        }
        cost += mbBudget;
        // TikTok — tablodaki satırla aynı hesap (tiktokBrandingTotals).
        if (tRes.ok) {
          const tRows = await tRes.json();
          if (Array.isArray(tRows)) cost += tiktokBrandingTotals(tRows, brandId).butce;
        }
      } catch (e) {
        console.error(`Monthly actual cost for ${month}:`, e);
      }
      actuals[month] = Math.round(cost);
    }));
    setMonthlyActualCosts(actuals);
  }, []);

  const fetchSavedMediaBuyingFormats = useCallback(async () => {
    const brandId = localStorage.getItem('currentBrandId');
    if (!brandId) {
      setSavedMediaBuyingFormats([]);
      return;
    }

    const periods = listYearMonthInRange(startDate, endDate);
    if (!periods.length) {
      setSavedMediaBuyingFormats([]);
      return;
    }

    setLoadingSavedMediaBuying(true);
    try {
      const results = await Promise.all(
        periods.map(async ({ year, month }) => {
          const res = await fetch(`${API}/api/brands/${brandId}/media-buying-format?year=${year}&month=${month}`, {
            credentials: 'include',
            headers: hdr(brandId),
          });
          if (!res.ok) {
            return { year, month, sections: [], ok: false, status: res.status };
          }
          const json = await res.json();
          return {
            year,
            month,
            ok: true,
            reportDate: json?.reportDate || null,
            updatedAt: json?.updatedAt || null,
            sections: Array.isArray(json?.sections) ? json.sections : [],
          };
        })
      );

      // Allowed brand list dışında kalan markalarda sessizce boş geç.
      if (results.some((r) => r.status === 403)) {
        setSavedMediaBuyingFormats([]);
        return;
      }

      const nonEmpty = results.filter((r) => r.ok && (r.sections?.length || 0) > 0);
      setSavedMediaBuyingFormats(nonEmpty);
    } catch (e) {
      console.error('Saved media buying formats:', e);
      setSavedMediaBuyingFormats([]);
    } finally {
      setLoadingSavedMediaBuying(false);
    }
  }, [startDate, endDate]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    await Promise.all([
      fetchGoogleBranding(),
      fetchMetaBranding(),
      fetchYouTubeData(),
      fetchYouTubePlacements(),
      fetchAdGroupVideos(),
      fetchManualBrandingTargets(),
      fetch2026BrandingBudget(),
      fetchMonthlyActualBrandingCosts(),
      fetchSavedMediaBuyingFormats(),
      fetchPinterestData(),
      fetchTikTokData(),
    ]);
    setLoading(false);
  }, [
    fetchGoogleBranding,
    fetchMetaBranding,
    fetchYouTubeData,
    fetchYouTubePlacements,
    fetchAdGroupVideos,
    fetchManualBrandingTargets,
    fetch2026BrandingBudget,
    fetchMonthlyActualBrandingCosts,
    fetchSavedMediaBuyingFormats,
    fetchPinterestData,
    fetchTikTokData,
  ]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  /* ── Hesaplamalar ──────────────────────────────────────────────────── */

  const mediaBuyingRow = useMemo(
    () => aggregateMediaBuyingFromSaved(savedMediaBuyingFormats),
    [savedMediaBuyingFormats]
  );

  const brandingRows = useMemo(() => {
    const rows = [];
    if (googleBranding) rows.push({
      label: 'Google (YouTube hariç)',
      // İzlenme buraya PMax/Demand Gen/Display'den geliyor; harcama ve gösterim
      // zaten bu satırda, o yüzden YouTube satırında tekrar sayılmıyor.
      data: { ...googleBranding, izlenme: nonVideoViews },
      campaigns: googleBrandingCampaigns,
    });
    if (youtubeTotal) rows.push({ label: 'YouTube (Google Ads)', data: youtubeTotal, campaigns: ytCreatives.map(v => ({ label: v.name, data: v })) });
    if (metaBranding)   rows.push({ label: 'Meta',   data: withDedupReach(metaBranding, reachOf), campaigns: withDedupReach(metaBrandingCampaigns, reachOf) });

    // Medya Satınalma: Report-6 saved data takes priority over planner
    if (mediaBuyingRow) {
      rows.push(mediaBuyingRow);
    } else {
      const manualMediaRow = manualBrandingRows.find(r => normalizeLabel(r.label).includes('medya'));
      if (manualMediaRow) rows.push(manualMediaRow);
    }

    // Pinterest: DB data takes priority over planner
    if (pinterestRow) {
      rows.push(pinterestRow);
    } else {
      const manualPintRow = manualBrandingRows.find(r => normalizeLabel(r.label).includes('pinterest'));
      if (manualPintRow) rows.push(manualPintRow);
    }

    // TikTok: live API data
    if (tiktokRow) {
      rows.push(tiktokRow);
    }

    // Any remaining manual rows not covered above
    const coveredLabels = ['medya', 'pinterest'];
    rows.push(...manualBrandingRows.filter(r => !coveredLabels.some(l => normalizeLabel(r.label).includes(l))));

    return rows;
  }, [googleBranding, metaBranding, googleBrandingCampaigns, metaBrandingCampaigns, youtubeTotal, nonVideoViews, ytCreatives, manualBrandingRows, mediaBuyingRow, pinterestRow, tiktokRow, reachOf]);

  const brandingTotal = useMemo(() => {
    const t = sumMetrics(brandingRows.map(r => r.data), BRANDING_COLS, reachOf);
    if (t.butce && t.gelir) t.roas = t.gelir / t.butce;
    return t;
  }, [brandingRows, reachOf]);

  const placementRows = useMemo(() => ytPlacements.map(p => ({ label: p.name, data: p })), [ytPlacements]);
  const visiblePlacementRows = useMemo(
    () => placementRows.slice(0, visiblePlacementCount),
    [placementRows, visiblePlacementCount]
  );
  const placementTotal = useMemo(() => {
    const t = sumCols(YT_COLS, ytPlacements);
    t.izlenmeOrani = (t.gosterim || 0) > 0 ? ((t.izlenme || 0) / t.gosterim) * 100 : 0;
    return t;
  }, [ytPlacements]);

  const videoRows = useMemo(() => ytCreatives.map(v => ({ label: v.name, data: v })), [ytCreatives]);
  const videoTotal = useMemo(() => {
    const t = sumCols(YT_COLS, ytCreatives);
    t.izlenmeOrani = (t.gosterim || 0) > 0 ? ((t.izlenme || 0) / t.gosterim) * 100 : 0;
    return t;
  }, [ytCreatives]);

  useEffect(() => {
    // New data geldiğinde listeyi ilk 15 satıra döndür (topPlacementsOnly ile max 15 kanal)
    setVisiblePlacementCount(15);
  }, [placementRows.length]);
  const youtubeSummaryRows = useMemo(() => {
    if (!youtubeTotal) return [];
    return [{
      label: 'Google - YouTube',
      data: youtubeTotal,
      campaigns: videoRows,
    }];
  }, [youtubeTotal, videoRows]);
  const compareInfoLabel = useMemo(() => {
    const fmtShort = (d) => d?.toLocaleDateString('tr-TR', { day: 'numeric', month: 'short', year: 'numeric' }) || '–';
    return `${fmtShort(compareStartDate)} - ${fmtShort(compareEndDate)} vs ${fmtShort(startDate)} - ${fmtShort(endDate)}`;
  }, [compareStartDate, compareEndDate, startDate, endDate]);
  const onDateRangeChange = useCallback((range) => {
    const [s, e] = range || [];
    setPendingStartDate(s || null);
    setPendingEndDate(e || null);
    if (s && e) {
      setStartDate(s);
      setEndDate(e);
      if (isCompareAuto) {
        const autoCmp = getComparisonPeriods(s, e);
        setCompareStartDate(autoCmp.prevStart);
        setCompareEndDate(autoCmp.prevEnd);
        setPendingCompareStartDate(autoCmp.prevStart);
        setPendingCompareEndDate(autoCmp.prevEnd);
      }
    }
  }, [isCompareAuto]);
  const onCompareDateRangeChange = useCallback((range) => {
    const [s, e] = range || [];
    setPendingCompareStartDate(s || null);
    setPendingCompareEndDate(e || null);
    if (s && e) {
      setCompareStartDate(s);
      setCompareEndDate(e);
      setIsCompareAuto(false);
    }
  }, []);
  const resetCompareToAuto = useCallback(() => {
    const autoCmp = getComparisonPeriods(startDate, endDate);
    setCompareStartDate(autoCmp.prevStart);
    setCompareEndDate(autoCmp.prevEnd);
    setPendingCompareStartDate(autoCmp.prevStart);
    setPendingCompareEndDate(autoCmp.prevEnd);
    setIsCompareAuto(true);
  }, [startDate, endDate]);

  /* ── Render ────────────────────────────────────────────────────────── */

  return (
    <MetaReachContext.Provider value={reachOf}>
    <div className="min-h-screen pb-12" ref={pdfRef}>
      {/* Header */}
      <div className="mb-6 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-slate-800 tracking-tight">Aylık Branding Raporu</h1>
          <p className="text-[11px] text-slate-400 mt-0.5">Branding kampanyalarının kanal bazlı performans özeti</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <ConsistencyBadge startDate={startDate} endDate={endDate} />
          <div className="flex items-center gap-1.5 bg-white border border-slate-200 rounded-lg px-2.5 py-1.5 shadow-sm">
            <Calendar size={12} className="text-slate-400" />
            <DatePicker
              selectsRange
              startDate={pendingStartDate}
              endDate={pendingEndDate}
              onChange={onDateRangeChange}
              dateFormat="dd MMM yyyy"
              className="text-[11px] text-slate-600 bg-transparent border-none outline-none w-[170px] cursor-pointer"
            />
          </div>
          <div className="relative">
            <button
              onClick={() => setShowComparePicker(v => !v)}
              className="flex items-center gap-1.5 bg-white border border-slate-200 rounded-lg px-3 py-1.5 text-[11px] text-slate-600 hover:bg-slate-50 shadow-sm transition-colors"
            >
              Compare {isCompareAuto ? '(Auto)' : '(Custom)'}
            </button>
            {showComparePicker && (
              <div className="absolute right-0 mt-2 z-30 w-[300px] bg-white border border-slate-200 rounded-lg shadow-lg p-3">
                <div className="text-[10px] text-slate-500 mb-2">Compare period</div>
                <div className="flex items-center gap-1.5 bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-1.5">
                  <Calendar size={12} className="text-slate-400" />
                  <DatePicker
                    selectsRange
                    startDate={pendingCompareStartDate}
                    endDate={pendingCompareEndDate}
                    onChange={onCompareDateRangeChange}
                    dateFormat="dd MMM yyyy"
                    className="text-[11px] text-slate-600 bg-transparent border-none outline-none w-[220px] cursor-pointer"
                  />
                </div>
                <div className="mt-2 flex justify-end gap-2">
                  <button
                    onClick={() => setShowComparePicker(false)}
                    className="px-2.5 py-1 text-[10px] rounded border border-slate-200 text-slate-500 hover:bg-slate-50"
                  >
                    Close
                  </button>
                  {!isCompareAuto && (
                    <button
                      onClick={resetCompareToAuto}
                      className="px-2.5 py-1 text-[10px] rounded border border-slate-200 text-slate-600 hover:bg-slate-50"
                    >
                      Compare Auto
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
          <button onClick={fetchAll} disabled={loading} className="flex items-center gap-1.5 bg-slate-800 text-white rounded-lg px-3 py-1.5 text-[11px] font-medium hover:bg-slate-700 shadow-sm transition-colors disabled:opacity-50">
            {loading ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
            Yenile
          </button>
          <button className="flex items-center gap-1.5 bg-white border border-slate-200 rounded-lg px-3 py-1.5 text-[11px] text-slate-600 hover:bg-slate-50 shadow-sm transition-colors">
            <Download size={12} /> Excel
          </button>
          <button
            onClick={handleExportPDF}
            disabled={exporting}
            className="flex items-center gap-1.5 bg-red-600 hover:bg-red-700 disabled:bg-gray-400 text-white rounded-lg px-3 py-1.5 text-[11px] font-medium shadow-sm transition-colors"
          >
            {exporting ? <Loader2 size={12} className="animate-spin" /> : <FileDown size={12} />}
            {exporting ? 'PDF...' : 'PDF'}
          </button>
        </div>
      </div>

      {/* 2026 Branding Targets (Monthly Target Planner) */}
      <div className="mb-6 rounded-lg border border-slate-200/80 overflow-hidden bg-white">
        <div className="bg-gradient-to-r from-slate-800 to-slate-700 px-3 py-2">
          <h3 className="text-[11px] font-semibold text-slate-200 uppercase tracking-wider">{TARGET_PLANNER_YEAR} Branding Targets (Monthly Target Planner)</h3>
          <p className="text-[10px] text-slate-400 mt-0.5">Biten aylar: gerçekleşen cost · Gelecek aylar: hedef</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-slate-50">
                {monthlyBrandingBudget.map((m, idx) => (
                  <th key={`budget-head-${m.month}`} className="px-3 py-2 text-center text-[10px] font-medium text-slate-500 uppercase tracking-wider whitespace-nowrap">
                    {m.month}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr>
                {monthlyBrandingBudget.map((m, idx) => {
                  const monthNum = idx + 1;
                  const actual = monthlyActualCosts[monthNum];
                  const showActual = actual != null;
                  const value = showActual ? actual : m.amount;
                  return (
                    <td key={`budget-val-${m.month}`} className={`px-3 py-2 text-center text-[11px] font-semibold tabular-nums border-t border-slate-100 whitespace-nowrap ${showActual ? 'text-emerald-700 bg-emerald-50/50' : 'text-slate-700'}`}>
                      {fmt(value, 'currency')}
                    </td>
                  );
                })}
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {loading && (
        <div className="fixed inset-0 bg-black/5 backdrop-blur-[1px] z-50 flex items-center justify-center">
          <div className="flex items-center gap-2.5 bg-white px-5 py-3 rounded-xl shadow-xl border border-slate-200">
            <Loader2 size={16} className="text-slate-600 animate-spin" />
            <span className="text-[12px] text-slate-600 font-medium">Branding verileri yükleniyor…</span>
          </div>
        </div>
      )}

      {/* KPI Cards */}
      <div className="mb-2 text-[11px] text-slate-500">
        Karşılaştırma dönemi: <span className="font-medium text-slate-700">{compareInfoLabel}</span>
      </div>
      <KpiCards brandingTotal={brandingTotal} />

      {/* 1. Branding Kanallar */}
      <Section icon={BarChart3} title="Branding Kanallar" subtitle="Branding, Demand Gen, Display ve YouTube kampanyaları">
        <BrandingChannelsTable
          columns={BRANDING_COLS}
          rows={brandingRows}
          totalRow={brandingTotal}
          expandedRows={expandedBrandingRows}
          onToggle={(rowLabel) => setExpandedBrandingRows(prev => ({ ...prev, [rowLabel]: !prev[rowLabel] }))}
          presetFilters={presetFilters}
        />
      </Section>

      {/* 2. YouTube Özet */}
      <Section iconSrc="/youtube.svg" title="YouTube" subtitle="Google Ads video kampanyaları toplam performansı">
        <BrandingChannelsTable
          columns={YT_COLS}
          rows={youtubeSummaryRows}
          totalRow={null}
          expandedRows={expandedYoutubeRows}
          onToggle={(rowLabel) => setExpandedYoutubeRows(prev => ({ ...prev, [rowLabel]: !prev[rowLabel] }))}
          presetFilters={presetFilters}
        />
      </Section>

      {/* 3. YouTube Kanalları */}
      <Section iconSrc="/youtube.svg" title="YouTube Kanalları" subtitle="Bütçeye göre sıralı (en çok harcama yapan kanallar)">
        <div
          className="h-[320px] overflow-y-auto"
          onScroll={(e) => {
            const el = e.currentTarget;
            const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 24;
            if (nearBottom && visiblePlacementCount < placementRows.length) {
              setVisiblePlacementCount((prev) => Math.min(prev + 40, placementRows.length));
            }
          }}
        >
          <DataTable label="Kanal" columns={YT_COLS} rows={visiblePlacementRows} totalRow={null} presetFilters={presetFilters} />
        </div>
        <div className="rounded-b-lg border-x border-b border-slate-200/80 overflow-x-auto">
          <table className="w-full border-collapse bg-gradient-to-r from-slate-900 to-slate-800">
            <tbody>
              <tr>
                <td className="px-3 py-2.5 text-[11px] font-bold text-white uppercase tracking-wide" style={{ minWidth: 180 }}>
                  Toplam
                </td>
                {YT_COLS.map(c => (
                  <td key={`yt-total-${c.key}`} className="px-3 py-2.5 text-right text-[11px] font-bold text-white tabular-nums whitespace-nowrap" style={{ minWidth: 100 }}>
                    {fmt(placementTotal?.[c.key], c.format)}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      </Section>

      {/* 4. YouTube Videoları */}
      <Section iconSrc="/youtube.svg" title="YouTube Kampanyaları" subtitle="Kampanya bazlı video performansı">
        <DataTable label="Kampanya" columns={YT_COLS} rows={videoRows} totalRow={videoTotal} presetFilters={presetFilters} />
        {Object.keys(adGroupVideos).length > 0 && (
          <div className="mt-2 rounded-lg border border-slate-200/80 bg-white overflow-hidden divide-y divide-slate-100">
            {Object.entries(adGroupVideos).map(([agName, agData]) => (
              agData.videoIds && agData.videoIds.length > 0 && (
                <div key={agName} className="px-3 py-2.5">
                  <div className="text-[11px] font-semibold text-slate-600 mb-1.5 truncate">{agName}</div>
                  <div className="flex items-center gap-2 flex-wrap">
                    {agData.videoIds.map(vid => (
                      <a key={vid} href={`https://www.youtube.com/watch?v=${vid}`} target="_blank" rel="noopener noreferrer"
                        className="group relative rounded-md border border-slate-200 overflow-hidden hover:border-red-300 hover:shadow-sm transition-all"
                      >
                        <img
                          src={`https://img.youtube.com/vi/${vid}/default.jpg`}
                          alt={agData.titles?.[vid] || vid}
                          className="w-[72px] h-[54px] object-cover"
                          loading="lazy"
                        />
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                          <svg className="w-4 h-4 text-white opacity-0 group-hover:opacity-100 transition-opacity drop-shadow" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>
                        </div>
                        {agData.impressions?.[vid] != null && (
                          <div className="absolute bottom-0 left-0 right-0 bg-black/60 px-1 py-0.5 text-[8px] text-white text-center leading-none font-medium tabular-nums">
                            {fmt(agData.impressions[vid], 'number')} gst.
                          </div>
                        )}
                        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 bg-slate-800 text-white text-[10px] rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10 max-w-[200px] truncate">
                          {agData.titles?.[vid] || vid}
                        </div>
                      </a>
                    ))}
                  </div>
                </div>
              )
            ))}
          </div>
        )}
      </Section>

      {/* 5. Kaydedilen Medya Satınalma Tabloları (Report-6) */}
      <Section
        icon={BarChart3}
        title="Kaydedilen Branding Tabloları"
        subtitle={`Seçili tarih aralığına göre getirilen kayıtlar (${savedMediaBuyingFormats.length} dönem)`}
      >
        {loadingSavedMediaBuying && (
          <div className="rounded-lg border border-slate-200/80 bg-white px-4 py-5 text-[11px] text-slate-500">
            Kaydedilen tablolar yükleniyor...
          </div>
        )}
        {!loadingSavedMediaBuying && savedMediaBuyingFormats.length === 0 && (
          <div className="rounded-lg border border-slate-200/80 bg-white px-4 py-5 text-[11px] text-slate-500">
            Seçili tarih aralığında kaydedilmiş branding tablosu bulunamadı.
          </div>
        )}
        {!loadingSavedMediaBuying && savedMediaBuyingFormats.length > 0 && (
          <div className="space-y-4">
            {savedMediaBuyingFormats.map((period) => (
              <div key={`saved-format-${period.year}-${period.month}`} className="rounded-lg border border-slate-200/80 overflow-hidden bg-white">
                <div className="px-3 py-2 border-b border-slate-200/80 bg-slate-50 flex items-center justify-between gap-2">
                  <div className="text-[11px] font-semibold text-slate-700">
                    {MONTH_LABELS_TR[(period.month || 1) - 1]} {period.year}
                  </div>
                  <div className="text-[10px] text-slate-500">
                    {period.updatedAt ? `Güncellendi: ${new Date(period.updatedAt).toLocaleString('tr-TR')}` : ''}
                  </div>
                </div>
                <div className="p-3 space-y-3">
                  {(period.sections || []).map((section, idx) => (
                    <SavedMediaBuyingTable
                      key={`saved-section-${period.year}-${period.month}-${idx}`}
                      title={section.title || `Bölüm ${idx + 1}`}
                      rows={section.rows || []}
                      total={section.total || null}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </Section>
    </div>
    </MetaReachContext.Provider>
  );
}
